/**
 * Business Plan Quality Scoring Types
 * Enloop-style scoring and validation system
 * These are completely optional - existing plans work without them
 */

import { SectionId } from './businessPlan';

export interface SectionScore {
  sectionId: SectionId;
  score: number; // 0-100
  status: 'excellent' | 'good' | 'needs-improvement' | 'incomplete';
  feedback: string[];
  missingElements: string[];
  strengths: string[];
  wordCount: number;
  recommendedWordCount: number;
}

export interface ValidationRule {
  id: string;
  type: 'required' | 'content-quality' | 'format' | 'completeness';
  description: string;
  status: 'pass' | 'fail' | 'warning';
  message: string;
  recommendation?: string;
}

export interface BusinessPlanScore {
  id: string;
  business_plan_id: string;
  user_id: string;
  
  // Overall metrics
  overall_score: number; // 0-100
  validation_status: 'pass' | 'fail' | 'needs-improvement';
  
  // Section-level details
  section_scores: Record<SectionId, SectionScore>;
  
  // Completeness
  completeness_score: number;
  sections_completed: number;
  sections_total: number;
  
  // Quality metrics
  content_quality_score: number;
  detail_score: number;
  
  // Validation feedback
  validation_feedback: ValidationRule[];
  missing_elements: string[];
  recommendations: string[];
  
  // Metadata
  calculated_at: string;
  created_at: string;
  updated_at: string;
}

export interface ScoringCriteria {
  minWordCount: number;
  recommendedWordCount: number;
  requiredElements: string[];
  qualityIndicators: string[];
  weight: number; // Relative importance of this section
}

export const SECTION_SCORING_CRITERIA: Record<SectionId, ScoringCriteria> = {
  'cover-page': {
    minWordCount: 50,
    recommendedWordCount: 100,
    requiredElements: ['business name', 'founder name', 'date'],
    qualityIndicators: ['professional formatting', 'complete contact information'],
    weight: 0.5
  },
  'table-of-contents': {
    minWordCount: 50,
    recommendedWordCount: 100,
    requiredElements: ['section list', 'page numbers'],
    qualityIndicators: ['all sections listed', 'accurate page numbers'],
    weight: 0.5
  },
  'overview': {
    minWordCount: 150,
    recommendedWordCount: 300,
    requiredElements: ['business concept', 'mission statement'],
    qualityIndicators: ['clear value proposition', 'target audience identified'],
    weight: 1.0
  },
  'executive-summary': {
    minWordCount: 300,
    recommendedWordCount: 500,
    requiredElements: ['business overview', 'market opportunity', 'financial highlights', 'funding request'],
    qualityIndicators: ['compelling narrative', 'key metrics included', 'clear ask'],
    weight: 2.0
  },
  'swot-analysis': {
    minWordCount: 200,
    recommendedWordCount: 400,
    requiredElements: ['strengths', 'weaknesses', 'opportunities', 'threats'],
    qualityIndicators: ['specific examples', 'realistic assessment', 'actionable insights'],
    weight: 1.5
  },
  'business-models': {
    minWordCount: 250,
    recommendedWordCount: 400,
    requiredElements: ['revenue streams', 'pricing strategy'],
    qualityIndicators: ['multiple revenue sources', 'scalability discussed', 'unit economics'],
    weight: 2.0
  },
  'viability-analysis': {
    minWordCount: 200,
    recommendedWordCount: 400,
    requiredElements: ['market validation', 'feasibility assessment'],
    qualityIndicators: ['data-driven analysis', 'risk mitigation strategies'],
    weight: 1.5
  },
  'market-research': {
    minWordCount: 400,
    recommendedWordCount: 800,
    requiredElements: ['target market', 'market size', 'competition', 'trends'],
    qualityIndicators: ['quantitative data', 'competitor analysis', 'market segmentation'],
    weight: 2.5
  },
  'products-services': {
    minWordCount: 300,
    recommendedWordCount: 600,
    requiredElements: ['product description', 'unique features', 'benefits'],
    qualityIndicators: ['clear differentiation', 'customer pain points addressed'],
    weight: 2.0
  },
  'sales-marketing': {
    minWordCount: 300,
    recommendedWordCount: 600,
    requiredElements: ['marketing channels', 'sales strategy', 'customer acquisition'],
    qualityIndicators: ['specific tactics', 'budget allocation', 'metrics defined'],
    weight: 2.0
  },
  'operations': {
    minWordCount: 250,
    recommendedWordCount: 500,
    requiredElements: ['operational processes', 'resources needed'],
    qualityIndicators: ['workflow defined', 'team structure', 'scalability plan'],
    weight: 1.5
  },
  'financials': {
    minWordCount: 400,
    recommendedWordCount: 800,
    requiredElements: ['revenue projections', 'expense breakdown', 'profitability timeline', 'funding needs'],
    qualityIndicators: ['3-5 year projections', 'realistic assumptions', 'break-even analysis'],
    weight: 3.0
  },
  'implementation-plan': {
    minWordCount: 250,
    recommendedWordCount: 500,
    requiredElements: ['timeline', 'milestones', 'key activities'],
    qualityIndicators: ['specific dates', 'measurable goals', 'resource allocation'],
    weight: 1.5
  }
};

export interface ScoreCalculationResult {
  score: BusinessPlanScore;
  summary: {
    totalScore: number;
    grade: 'A' | 'B' | 'C' | 'D' | 'F';
    strengths: string[];
    improvements: string[];
    nextSteps: string[];
  };
}
